import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { ReactNode, useEffect, useState } from 'react';
import { msalConfig, loginRequest } from './msalConfig';

// Create the MSAL instance (singleton) and initialize at module level.
// This ensures initialize() and handleRedirectPromise() run exactly once,
// avoiding the double-call caused by React 18 StrictMode re-mounting effects.
const msalInstance = new PublicClientApplication(msalConfig);
const msalInitPromise = msalInstance.initialize().then(() => {
    // Handle the redirect response (auth code exchange) before React renders MsalProvider.
    // This must happen once and only once per page load.
    return msalInstance.handleRedirectPromise();
}).then((response) => {
    if (response) {
        console.log('[MSAL] Redirect login successful, account:', response.account?.username);
    } else {
        console.log('[MSAL] No redirect response (first visit or already handled)');
    }
}).catch((error) => {
    console.error('[MSAL] Initialization/redirect error:', error);
    throw error; // Re-throw so the component can catch it
});

interface SalesAuthGuardProps {
    children: ReactNode;
}

/**
 * Loading component shown while MSAL initializes or authentication is in progress.
 */
function AuthLoading() {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontFamily: 'Trebuchet MS, sans-serif',
            color: '#025150',
        }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                    Authenticating...
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    Redirecting to Microsoft sign-in
                </div>
            </div>
        </div>
    );
}

/**
 * Error component shown when authentication fails.
 */
function AuthError({ error }: { error: unknown }) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown authentication error occurred.';
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontFamily: 'Trebuchet MS, sans-serif',
            color: '#791616',
        }}>
            <div style={{ textAlign: 'center', maxWidth: '500px', padding: '2rem' }}>
                <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                    Authentication Failed
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                    {errorMessage}
                </div>
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        padding: '0.5rem 1.5rem',
                        backgroundColor: '#025150',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontFamily: 'Trebuchet MS, sans-serif',
                    }}
                >
                    Try Again
                </button>
            </div>
        </div>
    );
}

/**
 * SalesAuthGuard wraps the Sales Care Homes page with Microsoft Entra ID authentication.
 * Initialization and redirect handling happen at module scope (once per page load).
 * MsalProvider renders only after MSAL is fully ready.
 */
export default function SalesAuthGuard({ children }: SalesAuthGuardProps) {
    const [isReady, setIsReady] = useState(false);
    const [initError, setInitError] = useState<unknown>(null);

    useEffect(() => {
        // Wait for the module-level init promise (already started, not re-triggered)
        msalInitPromise
            .then(() => setIsReady(true))
            .catch((error) => setInitError(error));
    }, []);

    if (initError) {
        return <AuthError error={initError} />;
    }

    if (!isReady) {
        return <AuthLoading />;
    }

    return (
        <MsalProvider instance={msalInstance}>
            <MsalAuthenticationTemplate
                interactionType={InteractionType.Redirect}
                authenticationRequest={loginRequest}
                loadingComponent={AuthLoading}
                errorComponent={({ error }) => <AuthError error={error} />}
            >
                {children}
            </MsalAuthenticationTemplate>
        </MsalProvider>
    );
}
